/**
 * WatchlistModeToggle component for switching between Manual and Sync modes
 * Includes warning for destructive mode changes
 */

export type WatchlistMode = 'manual' | 'sync';

interface WatchlistModeToggleProps {
  mode: WatchlistMode;
  onModeChange: (newMode: WatchlistMode) => void;
  hasLetterboxdUrl: boolean;
  filmsCount: number;
  disabled?: boolean;
}

export function WatchlistModeToggle({
  mode,
  onModeChange,
  hasLetterboxdUrl,
  filmsCount: _filmsCount,
  disabled = false,
}: WatchlistModeToggleProps) {
  // filmsCount is available for future use (e.g., showing warnings about film loss)
  void _filmsCount;
  const isManual = mode === 'manual';

  // Determine if switching to sync is possible
  const canSwitchToSync = hasLetterboxdUrl;

  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-gray-600">Mode:</span>
      <div className="relative flex rounded-lg bg-gray-100 p-0.5">
        {/* Manual button */}
        <button
          onClick={() => !isManual && onModeChange('manual')}
          disabled={disabled || isManual}
          className={`relative px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
            isManual
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
        >
          Manual
        </button>

        {/* Sync button */}
        <button
          onClick={() => isManual && canSwitchToSync && onModeChange('sync')}
          disabled={disabled || !isManual || !canSwitchToSync}
          title={
            !canSwitchToSync
              ? 'Add a Letterboxd URL to enable sync mode'
              : undefined
          }
          className={`relative px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
            !isManual
              ? 'bg-white text-gray-900 shadow-sm'
              : canSwitchToSync
                ? 'text-gray-500 hover:text-gray-700'
                : 'text-gray-400 cursor-not-allowed'
          } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
        >
          Sync
        </button>
      </div>

      {/* Mode description */}
      <span className="text-xs text-gray-500 hidden sm:inline">
        {isManual ? 'Add films manually' : 'Synced with Letterboxd'}
      </span>
    </div>
  );
}

interface ModeChangeWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  targetMode: WatchlistMode;
  filmsCount: number;
  isLoading?: boolean;
}

export function ModeChangeWarningModal({
  isOpen,
  onClose,
  onConfirm,
  targetMode,
  filmsCount,
  isLoading = false,
}: ModeChangeWarningModalProps) {
  if (!isOpen) return null;

  const isToManual = targetMode === 'manual';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-md transform rounded-lg bg-white p-6 shadow-xl transition-all">
          <div className="flex items-start gap-4">
            {/* Warning icon */}
            <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-yellow-100">
              <svg
                className="h-6 w-6 text-yellow-600"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
            </div>

            <div className="flex-1">
              <h3 className="text-lg font-medium text-gray-900">
                Switch to {isToManual ? 'Manual' : 'Sync'} Mode?
              </h3>

              <div className="mt-2 text-sm text-gray-500">
                {isToManual ? (
                  <>
                    <p>
                      Switching to manual mode will disconnect this watchlist
                      from Letterboxd.
                    </p>
                    <p className="mt-2">
                      Your current {filmsCount} film
                      {filmsCount !== 1 ? 's' : ''} will be kept, and you'll be
                      able to add or remove films manually.
                    </p>
                    <p className="mt-2 text-yellow-600 font-medium">
                      Automatic syncing will be disabled.
                    </p>
                  </>
                ) : (
                  <>
                    <p>
                      Switching to sync mode will replace your current films
                      with your Letterboxd watchlist.
                    </p>
                    {filmsCount > 0 && (
                      <p className="mt-2 text-red-600 font-medium">
                        Warning: Your {filmsCount} manually added film
                        {filmsCount !== 1 ? 's' : ''} will be replaced.
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50 ${
                isToManual
                  ? 'bg-primary hover:bg-primary-700'
                  : 'bg-yellow-600 hover:bg-yellow-700'
              }`}
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  Switching...
                </span>
              ) : (
                `Switch to ${isToManual ? 'Manual' : 'Sync'}`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
